import type { WorkItemListQuery } from '@deskworks/api-client';

export const qk = {
  me: ['me'] as const,
  accountWorkspaces: (accountId: string) => ['account', accountId, 'workspaces'] as const,
  accountDesks: (accountId: string) => ['account', accountId, 'desks'] as const,
  accountInbox: (accountId: string) => ['account', accountId, 'inbox'] as const,

  ws: (workspaceId: string) => ['ws', workspaceId] as const,
  workspace: (workspaceId: string) => ['ws', workspaceId, 'info'] as const,
  overview: (workspaceId: string) => ['ws', workspaceId, 'overview'] as const,
  activity: (workspaceId: string, deskId?: string, limit?: number) =>
    ['ws', workspaceId, 'activity', { deskId: deskId ?? null, limit: limit ?? null }] as const,
  activityAll: (workspaceId: string) => ['ws', workspaceId, 'activity'] as const,
  desks: (workspaceId: string) => ['ws', workspaceId, 'desks'] as const,
  workItems: (workspaceId: string, query: WorkItemListQuery = {}) => ['ws', workspaceId, 'workItems', query] as const,
  workItemsAll: (workspaceId: string) => ['ws', workspaceId, 'workItems'] as const,
  agents: (workspaceId: string) => ['ws', workspaceId, 'agents'] as const,
  connections: (workspaceId: string) => ['ws', workspaceId, 'connections'] as const,
  secrets: (workspaceId: string) => ['ws', workspaceId, 'secrets'] as const,
  skills: (workspaceId: string) => ['ws', workspaceId, 'skills'] as const,
  runtimeProfiles: (workspaceId: string) => ['ws', workspaceId, 'runtimeProfiles'] as const,
  approvals: (workspaceId: string, status?: string) => ['ws', workspaceId, 'approvals', status ?? null] as const,
  approvalsAll: (workspaceId: string) => ['ws', workspaceId, 'approvals'] as const,
  sessions: (workspaceId: string, deskId?: string) => ['ws', workspaceId, 'sessions', deskId ?? null] as const,
  sessionsAll: (workspaceId: string) => ['ws', workspaceId, 'sessions'] as const,
  setupConversations: (workspaceId: string) => ['ws', workspaceId, 'setupConversations'] as const,
  accountSetupConversations: (accountId: string) => ['account', accountId, 'setupConversations'] as const,
  frontDeskConversations: (accountId: string) => ['account', accountId, 'frontDeskConversations'] as const,
  frontDeskSettings: (accountId: string) => ['account', accountId, 'frontDeskSettings'] as const,
  frontDeskTasks: (accountId: string) => ['account', accountId, 'frontDeskTasks'] as const,
  frontDeskRuntimes: (accountId: string) => ['account', accountId, 'frontDeskRuntimes'] as const,
  dictation: (accountId: string) => ['account', accountId, 'dictation'] as const,
  liveVicki: (accountId: string) => ['account', accountId, 'liveVicki'] as const,
  vickiKnowledge: (accountId: string) => ['account', accountId, 'vickiKnowledge'] as const,
  vickiGoals: (accountId: string) => ['account', accountId, 'vickiGoals'] as const,
  vickiMemory: (accountId: string, id: string) => ['account', accountId, 'vickiMemory', id] as const,
  vickiActions: (conversationId: string) => ['conversation', conversationId, 'vickiActions'] as const,
  vickiDrive: (conversationId: string) => ['conversation', conversationId, 'vickiDrive'] as const,
  vickiTaskActions: (workItemId: string) => ['workItem', workItemId, 'vickiActions'] as const,
  resources: (workspaceId: string) => ['ws', workspaceId, 'resources'] as const,
  deskStore: (workspaceId: string) => ['ws', workspaceId, 'deskStore'] as const,
  deskStorePlan: (workspaceId: string, entryId: string) => ['ws', workspaceId, 'deskStore', entryId, 'plan'] as const,

  desk: (deskId: string) => ['desk', deskId] as const,
  deskWorkItemStats: (deskId: string) => ['desk', deskId, 'workItemStats'] as const,
  deskConversations: (deskId: string) => ['deskConversations', deskId] as const,
  /** Desks talk to each other (docs/features/desk-talk.md). */
  deskQuestions: (deskId: string) => ['deskQuestions', deskId] as const,
  vickiTalks: (accountId: string) => ['account', accountId, 'vickiTalks'] as const,
  // Under the chat's key: a message there (the answer comes as one) refreshes its questions too.
  chatQuestions: (conversationId: string) => ['conversation', conversationId, 'questions'] as const,
  deskTemplate: (deskId: string) => ['desk', deskId, 'template'] as const,
  deskMovePlan: (deskId: string, workspaceId: string) => ['desk', deskId, 'movePlan', workspaceId] as const,
  // Under the desk/conversation key so status changes of their runs refresh the session too.
  deskTranscript: (deskId: string) => ['desk', deskId, 'transcript'] as const,
  deskLearnings: (deskId: string) => ['desk', deskId, 'learnings'] as const,
  deskTraining: (deskId: string) => ['desk', deskId, 'training'] as const,
  // A desk's files (docs/features/desk-files.md); the realtime event desk.files makes all of them stale.
  deskFiles: (deskId: string) => ['deskFiles', deskId] as const,
  deskFileTree: (deskId: string) => ['deskFiles', deskId, 'tree'] as const,
  deskFileHistory: (deskId: string, path?: string) => ['deskFiles', deskId, 'history', path ?? null] as const,
  deskFileWorkItems: (deskId: string) => ['deskFiles', deskId, 'workItems'] as const,
  // A content by its git id, or a path's in a commit, never changes: outside deskFiles, so no event refetches it.
  deskBlob: (deskId: string, sha: string, as: 'text' | 'blob') => ['deskBlob', deskId, sha, as] as const,
  deskFileAt: (deskId: string, commit: string, path: string) => ['deskBlob', deskId, commit, 'at', path] as const,
  conversation: (conversationId: string) => ['conversation', conversationId] as const,
  conversationTranscript: (conversationId: string) => ['conversation', conversationId, 'transcript'] as const,
  conversationActions: (conversationId: string) => ['conversation', conversationId, 'actions'] as const,
  setupDraft: (conversationId: string) => ['conversation', conversationId, 'setupDraft'] as const,
  agent: (agentId: string) => ['agent', agentId] as const,
  agentAll: ['agent'] as const,
  connection: (connectionId: string) => ['connection', connectionId] as const,
  skill: (skillId: string) => ['skill', skillId] as const,
  skillFile: (skillId: string, path: string) => ['skill', skillId, 'file', path] as const,
  mcpTools: (connectionId: string) => ['connection', connectionId, 'mcpTools'] as const,
  scopeChoices: (connectionId: string) => ['connection', connectionId, 'scopeChoices'] as const,
  apiTokens: (accountId: string) => ['account', accountId, 'apiTokens'] as const,
  mcpApps: (accountId: string) => ['account', accountId, 'mcpApps'] as const,

  usage: (workspaceId: string, from: string, to: string) => ['ws', workspaceId, 'usage', from, to] as const,
  usageAll: (workspaceId: string) => ['ws', workspaceId, 'usage'] as const,
  reports: (workspaceId: string, deskId?: string) => ['ws', workspaceId, 'reports', deskId ?? null] as const,
  reportsAll: (workspaceId: string) => ['ws', workspaceId, 'reports'] as const,
  workItem: (id: string) => ['workItem', id] as const,
  workItemDetail: (id: string) => ['workItem', id, 'detail'] as const,
  workItemActivity: (id: string) => ['workItem', id, 'activity'] as const,
  workItemMessages: (id: string) => ['workItem', id, 'messages'] as const,
  workItemTranscript: (id: string) => ['workItem', id, 'transcript'] as const,
  workItemExecutions: (id: string) => ['workItem', id, 'executions'] as const,
  workItemArtifacts: (id: string) => ['workItem', id, 'artifacts'] as const,
  workItemHistory: (id: string) => ['workItem', id, 'history'] as const,
  workItemFiles: (id: string) => ['workItem', id, 'files'] as const,
  runtimeLogin: (loginId: string) => ['runtimeLogin', loginId] as const,
  executionLogs: (executionId: string) => ['execution', executionId, 'logs'] as const,
  session: (executionId: string) => ['execution', executionId, 'session'] as const,
};
